import React, { useState, useEffect, useRef } from "react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";

/* ══════════════════════════════════════════════════════════════════════
   MultiSelectDropdown
   Generic searchable multi-select with removable chips. Used for the
   "Product Description" field in LeadTable, where a user picks one or
   more products belonging to the currently-selected Category.

   Props:
   - options: [{ label, value }]
   - values:  array of currently selected `value`s
   - onChange(newValuesArray)
   - placeholder
   - disabled
══════════════════════════════════════════════════════════════════════ */
const MultiSelectDropdown = ({
  options = [],
  values = [],
  onChange = () => {},
  placeholder = "Select...",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const h = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = options.filter((o) =>
    (o.label || "").toLowerCase().includes(query.toLowerCase())
  );

  const toggleValue = (val) => {
    if (values.includes(val)) {
      onChange(values.filter((v) => v !== val));
    } else {
      onChange([...values, val]);
    }
  };

  const removeValue = (val, e) => {
    e.stopPropagation();
    onChange(values.filter((v) => v !== val));
  };

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => !disabled && setOpen((o) => !o)}
        className={
          "w-full min-h-10 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 text-sm " +
          "flex flex-wrap items-center gap-1.5 transition-colors " +
          (disabled
            ? "opacity-50 cursor-not-allowed bg-slate-100"
            : "cursor-pointer hover:border-blue-300 focus-within:ring-2 focus-within:ring-blue-400")
        }
      >
        {values.length === 0 && (
          <span className="text-slate-400 text-sm py-1">{placeholder}</span>
        )}
        {values.map((v) => {
          const opt = options.find((o) => o.value === v);
          return (
            <span
              key={v}
              className="flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-2 py-0.5 text-xs font-medium"
            >
              {opt ? opt.label : v}
              {!disabled && (
                <XMarkIcon
                  className="w-3 h-3 cursor-pointer hover:text-blue-900"
                  onClick={(e) => removeValue(v, e)}
                />
              )}
            </span>
          );
        })}
        {!disabled && (
          <ChevronDownIcon className="w-4 h-4 text-slate-400 ml-auto flex-shrink-0" />
        )}
      </div>

      {open && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 z-40 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
          <input
            autoFocus
            className="w-full px-3 py-2 text-sm border-b border-slate-100 focus:outline-none"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <div className="max-h-48 overflow-y-auto att-scrollbar">
            {filtered.length === 0 && (
              <div className="px-3 py-2 text-sm text-slate-400">
                No options found
              </div>
            )}
            {filtered.map((o) => (
              <div
                key={o.value}
                onClick={() => toggleValue(o.value)}
                className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50"
              >
                <input
                  type="checkbox"
                  checked={values.includes(o.value)}
                  readOnly
                  className="rounded border-slate-300"
                />
                <span className="text-slate-700">{o.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;